/**
 * Chat at the document (Spec 128, Epic #524) — thin wrapper around `ApiCore`.
 * Errors are handled locally by the chat panel (its own toast per S24, silence for
 * `chat.disabled` per S15), so the global error handler is suppressed for every call.
 */
import { get } from 'svelte/store';
import { locale } from 'svelte-i18n';
import type { ApiCore } from './core';

export type ChatRole = 'user' | 'model';
export type ChatMessageStatus = 'pending' | 'answered' | 'failed';
/** What a message is for (Spec 130 E17, Spec 134); `text` for everything outside a skill flow. */
export type ChatMessageKind =
  | 'text'
  | 'fact-question'
  | 'fact-answer'
  | 'fact-abandoned'
  | 'assessment'
  | 'template'
  | 'skill-offer';

/** A fixed statement of the skill with the primary source it rests on (Spec 133 E5). */
export interface DeadlineBlock {
  text: string;
  sourceRef: string;
  sourceUrl: string;
}

/** A place to turn to; `url` is `null` for entries without a web page (Spec 133 E15). */
export interface ChatReferral {
  name: string;
  url: string | null;
}

/**
 * The code-computed assessment of an `assessment` message (Spec 130 E6, Spec 133 E8); dates in `deadlines` are ISO.
 * `referrals` is empty unless `severity` is `edge`.
 */
export interface DeadlineHead {
  caseId: string;
  severity: 'normal' | 'edge';
  headline: string;
  blocks: DeadlineBlock[];
  evidenceTips: string[];
  assumptions: string;
  notConsidered: string[];
  referrals: ChatReferral[];
  deadlines: Record<string, string>;
  followupDeadlineId: string | null;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** `null` on model messages. */
  authorUserId: string | null;
  authorDisplayName: string | null;
  status: ChatMessageStatus;
  postedAt: string;
  /** Skill (and YAML version) that produced a model message; `null` for the base chat (Spec 129 E6). */
  skillId: string | null;
  skillVersion: number | null;
  kind: ChatMessageKind;
  factId: string | null;
  deadlineHead: DeadlineHead | null;
  /** ISO date the server proposed for a `template` message; `null` on every other message (Spec 133 E10). */
  replyDeadline: string | null;
  /** The AI stopped at its token limit; the answer is cut off (Spec 137 E3). */
  truncated: boolean;
  /** ISO date the answer offers as follow-up; `null` without an offer (Spec 145 E3). */
  followupOffer: string | null;
  /** Spec 146 E3: on the question for the first text fact, the user's message before the skill offer; `null` otherwise. */
  inputSuggestion?: string | null;
  /** Spec 148 E2: on the question for an item fact with several articles, one entry per article; `null`/absent otherwise. */
  choices?: string[] | null;
}

/** A follow-up letter button of a skill, label in the language of the request (Spec 146 E7). */
export interface FollowUpTemplate {
  id: string;
  label: string;
}

/** A skill that applies to the document, already localized by the backend (Spec 129 E5). */
export interface AvailableSkill {
  id: string;
  name: string;
  description: string;
  /** Empty without follow-up templates (Spec 146 E7). */
  followUpTemplates: FollowUpTemplate[];
}

export interface ChatHistory {
  /** The session the messages belong to (Spec 143); `null` for a document without a session. */
  sessionId: string | null;
  messages: ChatMessage[];
  availableSkills: AvailableSkill[];
  /** Answered skill facts, factId to value (Spec 130). */
  facts: Record<string, string>;
}

/** One entry of the session list, last used first (Spec 143 E4); `title` is `yyyy-MM-dd-{topic}`. */
export interface ChatSessionSummary {
  id: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
}

/** The follow-up a skill offer created (Spec 130 E11). */
export interface ChatFollowup {
  id: string;
  dueDate: string;
  subject: string;
}

const SUPPRESS_GLOBAL_ERROR_HANDLER = true;

/** App language for server-localized parts of the answer (skill names, Spec 129 S16); the browser header may differ. */
function acceptLanguage(): Record<string, string> {
  return { 'Accept-Language': (get(locale) ?? '').startsWith('en') ? 'en' : 'de' };
}

export class ChatApi {
  constructor(private readonly core: ApiCore) {}

  private chatUrl(documentId: string): string {
    return `${this.core.baseUrl}/api/v1/documents/${encodeURIComponent(documentId)}/chat`;
  }

  /** Without a session the server answers with the last used one (Spec 143 E5). */
  async getHistory(documentId: string, sessionId?: string): Promise<ChatHistory> {
    const query = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
    return this.core.fetchJson<ChatHistory>(
      `${this.chatUrl(documentId)}${query}`,
      { headers: { ...this.core.getAuthHeaders(), ...acceptLanguage() } },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  /** The sessions of a document, last used first (Spec 143). */
  async getSessions(documentId: string): Promise<ChatSessionSummary[]> {
    return this.core.fetchJson<ChatSessionSummary[]>(
      `${this.chatUrl(documentId)}/sessions`,
      { headers: { ...this.core.getAuthHeaders(), ...acceptLanguage() } },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  /**
   * `skillId` is sent only for a click on a skill suggestion (Spec 129 E8). Without `sessionId` the server starts a new
   * session (Spec 143 E6).
   */
  async postMessage(
    documentId: string,
    text: string,
    skillId?: string,
    sessionId?: string,
  ): Promise<ChatHistory> {
    return this.postJson<ChatHistory>(`${this.chatUrl(documentId)}/messages`, {
      text,
      ...(skillId ? { skillId } : {}),
      ...(sessionId ? { sessionId } : {}),
    });
  }

  /** The session is required by the server (Spec 143 S14); without one the call keeps the old wire and gets `chat.session_required`. */
  async retryMessage(
    documentId: string,
    messageId: string,
    sessionId: string,
  ): Promise<ChatHistory> {
    return this.core.fetchJson<ChatHistory>(
      `${this.chatUrl(documentId)}/messages/${encodeURIComponent(messageId)}/retry${sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''}`,
      { method: 'POST', headers: { ...this.core.getAuthHeaders(), ...acceptLanguage() } },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }

  /** The button "Datum angeben" (Spec 134 E6): asks the first open question of the skill again; the text is the visible user message. */
  async resumeQuestion(
    documentId: string,
    text: string,
    skillId: string,
    sessionId?: string,
  ): Promise<ChatHistory> {
    return this.postJson<ChatHistory>(`${this.chatUrl(documentId)}/messages`, {
      text,
      skillId,
      intent: 'resume',
      ...(sessionId ? { sessionId } : {}),
    });
  }

  /** The button "Mängelanzeige erstellen" (Spec 130 E8): the text is the visible user message. */
  async requestTemplate(
    documentId: string,
    text: string,
    sessionId?: string,
  ): Promise<ChatHistory> {
    return this.postJson<ChatHistory>(`${this.chatUrl(documentId)}/messages`, {
      text,
      intent: 'template',
      ...(sessionId ? { sessionId } : {}),
    });
  }

  /** A follow-up template button (Spec 146 E5): the code renders the letter without AI; the text is the visible user message. */
  async requestFollowUpTemplate(
    documentId: string,
    text: string,
    skillId: string,
    templateId: string,
    sessionId?: string,
  ): Promise<ChatHistory> {
    return this.postJson<ChatHistory>(`${this.chatUrl(documentId)}/messages`, {
      text,
      skillId,
      intent: `template:${templateId}`,
      ...(sessionId ? { sessionId } : {}),
    });
  }

  /**
   * Accepts a follow-up offer of the chat: of a skill deadline (Spec 130 E11/E12) or of an answer's date (Spec 145 E4).
   * `followup.active_exists` opens the replace question.
   */
  async createFollowup(
    documentId: string,
    target: { deadlineId: string } | { messageId: string },
    replace: boolean,
    sessionId: string,
  ): Promise<ChatFollowup> {
    return this.postJson<ChatFollowup>(`${this.chatUrl(documentId)}/followup`, {
      ...target,
      replace,
      sessionId,
    });
  }

  private postJson<T>(url: string, body: unknown): Promise<T> {
    return this.core.fetchJson<T>(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.core.getAuthHeaders(),
          ...acceptLanguage(),
        },
        body: JSON.stringify(body),
      },
      SUPPRESS_GLOBAL_ERROR_HANDLER,
    );
  }
}
